import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTrips, lastTripitRun, daysUntil } from '../lib/api';
import type { Trip, TripitRun } from '../lib/types';
import { TripCard } from '../components/TripCard';
import { WatercolorBloom } from '../components/WatercolorBloom';
import { IconArrowRight, IconGlobe, IconLightning } from '../components/Icons';

export default function Dashboard() {
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [run, setRun] = useState<TripitRun | null>(null);
  const [filter, setFilter] = useState<'all' | Trip['status']>('all');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, r] = await Promise.all([listTrips(), lastTripitRun()]);
        setTrips(t);
        setRun(r);
      } catch (e: any) {
        setErr(e?.message ?? 'Failed to load');
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!trips) return [];
    if (filter === 'all') return trips;
    return trips.filter(t => t.status === filter);
  }, [trips, filter]);

  const stats = useMemo(() => {
    const list = trips ?? [];
    const upcoming = list.filter(t => {
      const u = daysUntil(t.start_date);
      return u !== null && u >= 0 && u <= 30 && t.status !== 'completed' && t.status !== 'archived';
    });
    const booked = list.filter(t => t.status === 'booked' || t.status === 'in_progress');
    const ideas  = list.filter(t => t.status === 'idea' || t.status === 'researching');
    return { total: list.length, upcoming: upcoming.length, booked: booked.length, ideas: ideas.length };
  }, [trips]);

  return (
    <>
      <HeroPanel stats={stats} run={run} />
      <FilterBar filter={filter} setFilter={setFilter} />
      {err && <div className="card" style={{ borderColor: '#FF2BD6', marginTop: 16 }}><span className="label-bright">Error</span><br />{err}</div>}
      {trips === null ? (
        <LoadingState />
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24, marginTop: 24 }}>
          {filtered.map(t => <TripCard key={t.id} trip={t} />)}
        </div>
      )}
    </>
  );
}

function HeroPanel({ stats, run }: { stats: { total: number; upcoming: number; booked: number; ideas: number }, run: TripitRun | null }) {
  const now = new Date();
  const date = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const tripitLine = run
    ? `TripIt synced ${timeAgo(run.ran_at)} — ${run.trips_seen} trips · ${run.status.toUpperCase()}`
    : 'TripIt sync not yet run';

  return (
    <section className="card card-dark brackets" style={{ position: 'relative', overflow: 'hidden', minHeight: 200, padding: 32 }}>
      <WatercolorBloom variant="duo"   size={520} opacity={0.55} style={{ position: 'absolute', top: -160, right: -120, zIndex: 0 }} />
      <WatercolorBloom variant="uv"    size={420} opacity={0.4}  style={{ position: 'absolute', bottom: -180, left: -100, zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <span className="label" style={{ color: 'var(--c-cyan)' }}>// SYSTEM ONLINE · {date}</span>
          <h1 className="h1" style={{ marginTop: 8, color: 'white' }}>
            Paint the future<br/>
            <span className="gradient-text">of every trip.</span>
          </h1>
          <p className="body" style={{ color: 'var(--c-lavender)', maxWidth: 540, marginTop: 16 }}>
            From idea to landing — Atlas is the one place your trips live. Sketch new journeys, watch booked ones approach, and let AI extract every link you drop.
          </p>

          <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/new" className="btn btn-primary">Sketch a new journey <IconArrowRight size={16} /></Link>
            <Link to="/settings" className="btn btn-ghost" style={{ color: 'var(--c-lavender)', borderColor: 'rgba(232,217,255,0.4)' }}>System status →</Link>
          </div>
        </div>

        <StatGrid stats={stats} tripitLine={tripitLine} />
      </div>
    </section>
  );
}

function StatGrid({ stats, tripitLine }: any) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: 12, minWidth: 280 }}>
      <Stat n={stats.total} label="Total trips" />
      <Stat n={stats.upcoming} label="In next 30d" accent="cyan" />
      <Stat n={stats.booked} label="Booked / live" accent="lime" />
      <Stat n={stats.ideas} label="Ideas brewing" accent="magenta" />
      <div style={{ gridColumn: '1 / -1', marginTop: 6 }}>
        <span className="label" style={{ color: 'rgba(232,217,255,0.7)' }}>{tripitLine}</span>
      </div>
    </div>
  );
}

function Stat({ n, label, accent }: { n: number; label: string; accent?: 'cyan'|'lime'|'magenta' }) {
  const color = accent === 'cyan' ? 'var(--c-cyan)' : accent === 'lime' ? 'var(--c-lime)' : accent === 'magenta' ? 'var(--c-magenta)' : 'white';
  return (
    <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(232,217,255,0.2)', borderRadius: 10, padding: '12px 14px' }}>
      <div className="mono-num" style={{ fontSize: 28, color, lineHeight: 1 }}>{n}</div>
      <div className="label" style={{ color: 'rgba(232,217,255,0.85)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function FilterBar({ filter, setFilter }: any) {
  const opts: Array<'all' | Trip['status']> = ['all','idea','researching','planning','booked','in_progress','completed'];
  const labels: Record<string, string> = { all: 'All', idea: 'Ideas', researching: 'Research', planning: 'Planning', booked: 'Booked', in_progress: 'Live', completed: 'Done' };
  return (
    <div className="row-between" style={{ marginTop: 24, marginBottom: 4, flexWrap: 'wrap', gap: 12 }}>
      <h2 className="h3">// Your journeys</h2>
      <div className="tabs">
        {opts.map(o => (
          <button key={o} onClick={() => setFilter(o)} className={`tab ${filter === o ? 'active' : ''}`}>{labels[o]}</button>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: 48 }}>
      <span className="label">// Loading signal…</span>
    </div>
  );
}

function EmptyState({ filter }: { filter: string }) {
  return (
    <div className="card brackets" style={{ marginTop: 24, textAlign: 'center', padding: 48 }}>
      <span className="label-bright">// {filter.toUpperCase()}</span>
      <h3 className="h3" style={{ marginTop: 12 }}>No trips here yet.</h3>
      <p className="body muted" style={{ maxWidth: 420, margin: '12px auto 20px' }}>Drop a link, sketch an idea, or wait for the next TripIt sync to populate booked trips.</p>
      <Link to="/new" className="btn btn-primary">Sketch a new journey <IconArrowRight size={16} /></Link>
    </div>
  );
}

function timeAgo(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}
